import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiCreateEngineModelPotentialIssue, DigitaiEngineModelPotentialIssue, DigitaiUpdateEngineModelPotentialIssueById, DigitaiUpdateEngineModelsPotentialIssues } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/engine-model-potential-issue';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EngineModelPotentialIssueService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiEngineModelPotentialIssue> | null> = new BehaviorSubject(null);
    engineModelPotentialIssueSubject$: BehaviorSubject<DigitaiEngineModelPotentialIssue | null> = new BehaviorSubject(null);
    engineModelsPotentialIssuesSubject$: BehaviorSubject<DigitaiEngineModelPotentialIssue[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiEngineModelPotentialIssue> | null>; } = {};
    engineModelPotentialIssueScoped: { [key: string]: BehaviorSubject<DigitaiEngineModelPotentialIssue | null>; } = {};
    engineModelsPotentialIssuesScoped: { [key: string]: BehaviorSubject<DigitaiEngineModelPotentialIssue[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiEngineModelPotentialIssue>>
    {
        return this.paginationSubject$.asObservable();
    }

    get engineModelPotentialIssue$(): Observable<DigitaiEngineModelPotentialIssue>
    {
        return this.engineModelPotentialIssueSubject$.asObservable();
    }

    get engineModelsPotentialIssues$(): Observable<DigitaiEngineModelPotentialIssue[]>
    {
        return this.engineModelsPotentialIssuesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiEngineModelPotentialIssue>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiEngineModelPotentialIssue>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeEngineModelPotentialIssue(scope: string, object: DigitaiEngineModelPotentialIssue): void
    {
        if (this.engineModelPotentialIssueScoped[scope])
        {
            this.engineModelPotentialIssueScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.engineModelPotentialIssueScoped[scope] = new BehaviorSubject(object);
    }

    getScopeEngineModelPotentialIssue(scope: string): Observable<DigitaiEngineModelPotentialIssue>
    {
        if (this.engineModelPotentialIssueScoped[scope]) return this.engineModelPotentialIssueScoped[scope].asObservable();

        this.engineModelPotentialIssueScoped[scope] = new BehaviorSubject(null);
        return this.engineModelPotentialIssueScoped[scope].asObservable();
    }

    setScopeEngineModelsPotentialIssues(scope: string, objects: DigitaiEngineModelPotentialIssue[]): void
    {
        if (this.engineModelsPotentialIssuesScoped[scope])
        {
            this.engineModelsPotentialIssuesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.engineModelsPotentialIssuesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeEngineModelsPotentialIssues(scope: string): Observable<DigitaiEngineModelPotentialIssue[]>
    {
        if (this.engineModelsPotentialIssuesScoped[scope]) return this.engineModelsPotentialIssuesScoped[scope].asObservable();

        this.engineModelsPotentialIssuesScoped[scope] = new BehaviorSubject(null);
        return this.engineModelsPotentialIssuesScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiEngineModelPotentialIssue>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiEngineModelPotentialIssue>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            engineModelId = null,
            potentialIssueId = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            engineModelId?: string;
            potentialIssueId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModelPotentialIssue;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModelPotentialIssue;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    engineModelId,
                    potentialIssueId,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelPotentialIssue(scope, data.object) : this.engineModelPotentialIssueSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModelPotentialIssue;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModelPotentialIssue;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelPotentialIssue(scope, data.object) : this.engineModelPotentialIssueSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiEngineModelPotentialIssue[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiEngineModelPotentialIssue[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelsPotentialIssues(scope, data.objects) : this.engineModelsPotentialIssuesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateEngineModelPotentialIssue;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateEngineModelPotentialIssue[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModelPotentialIssueById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModelsPotentialIssues;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            engineModelId = null,
            potentialIssueId = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            engineModelId?: string;
            potentialIssueId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    engineModelId,
                    potentialIssueId,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
